import Echart from "echarts-for-react"
import { useMemo, useRef, useState, useCallback } from "react"
import { barChartStore } from "@/routes/apt/[apt_code]/statistics/_components/bar-chart/bar-chart-store"
import { statsStore } from "../../stats-store"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { langStore } from "@/stores/lang-store"
import useIsMobile from "@/hooks/use-is-mobile"
import ChevronButton from "@/components/buttons/chevron-button"
import { barChartBundle, PAGE_SIZE } from "./bar-chart-const"
import { clamp } from "../../stats-util"

const BarChart = ({ title = "수도 사용량", className = "", axisMax }) => {
  const chartRef = useRef(null)
  const navigate = useNavigate()
  const { region, aptCode } = useParams()
  const [searchParams] = useSearchParams()
  const resource = searchParams.get("resource")

  const { unit } = statsStore()
  const { xAxis, series } = barChartStore()

  const { lang } = langStore()
  const isMobile = useIsMobile()

  const len = xAxis?.length ?? 0
  const maxStart = isMobile ? Math.max(0, len - PAGE_SIZE) : 0

  const [startIndex, setStartIndex] = useState(0)

  const endIndex = clamp(startIndex + PAGE_SIZE - 1, 0, Math.max(0, len - 1))

  const option = {
    grid: {
      top: isMobile ? 70 : 80,
      left: isMobile ? "12%" : 40,
      right: isMobile ? "6%" : 10,
      bottom: 30
    },

    title: { text: title },

    tooltip: {
      trigger: "item",
      formatter: (p) => `
      <span style="color:${p.color}">●</span>
      <span>
        ${p.data.unit_id}<br/>
        ${barChartBundle.usage[lang]} : ${p.value.toFixed(2)} ${unit}
      </span>
    `
    },

    legend: {
      show: false
    },

    xAxis: {
      type: "category",
      data: xAxis,
      axisLabel: {
        rotate: 0,
        hideOverlap: true
      }
    },

    yAxis: {
      type: "value",
      name: `${barChartBundle.usage[lang]} [${unit}]`,
      max: axisMax,
      splitLine: {
        lineStyle: {
          type: "dashed"
        }
      }
    },

    series,

    dataZoom: [
      {
        type: "inside",

        startValue: isMobile ? startIndex : 0,
        endValue: isMobile ? endIndex : Math.max(0, len - 1),

        zoomOnMouseWheel: isMobile ? false : true,
        moveOnMouseWheel: false,
        moveOnMouseMove: isMobile ? false : true
      },
      {
        type: "slider",
        show: false
      }
    ]
  }

  const page = len ? Math.floor(startIndex / PAGE_SIZE) + 1 : 1
  const totalPages = len ? Math.ceil(len / PAGE_SIZE) : 1

  const prev = () => setStartIndex((v) => clamp(v - PAGE_SIZE, 0, maxStart))
  const next = () => setStartIndex((v) => clamp(v + PAGE_SIZE, 0, maxStart))

  const moveToUnitPage = useCallback(
    (unitId) => {
      if (!unitId || !region || !aptCode) return
      const [dong, ho] = String(unitId).split("-")
      if (!dong || !ho) return
      navigate(`/apt/${region}/${aptCode}/units?resource=${resource}&dong=${+dong}&ho=${+ho}`)
    },
    [aptCode, navigate, region, resource]
  )

  const onEvents = useMemo(
    () => ({
      dblclick: (params) => {
        moveToUnitPage(params?.data?.unit_id)
      }
    }),
    [moveToUnitPage]
  )

  return (
    <section className={`${className} flex flex-col`}>
      <div className="flex-1">
        <Echart
          ref={chartRef}
          option={option}
          style={{
            width: "100%",
            height: "100%"
          }}
          notMerge
          onEvents={onEvents}
          autoResize
        />
      </div>

      {isMobile && len > PAGE_SIZE && (
        <div className="flex justify-center items-center gap-2">
          <ChevronButton
            onClick={prev}
            disabled={startIndex === 0}
            className="border border-gray-200"
          />

          <div className="text-xs text-gray-600 min-w-16 text-center">
            {page} / {totalPages}
          </div>

          <ChevronButton
            onClick={next}
            disabled={startIndex >= maxStart}
            className="border border-gray-200"
            direction="right"
          />
        </div>
      )}
    </section>
  )
}

export default BarChart
